import pdf2md from "@opendocsg/pdf2md";
import * as Dialog from "../framework/dialog";
import { uploadFile } from "../framework/uploader";
import { getUITranslation } from "../utils/get_ui_translation";

const HUGE_PDF_LIMIT = 200000;

/**
 * This hook is responsible for handling the PDF file input, converting the PDF to markdown and enabling the submit button if the conversion is successful.
 * It's used with PDFUploaderComponent.
 */
const hook = {
  mounted() {
    const form = this.el.closest("form");
    const input = this.el;

    this.el.addEventListener("change", async (event) => {
      if (event.target === input) {
        try {
          await onChangeImporterPDFInput(event.target, form);
        } catch (err) {
          console.error("Import failed", err);
        }
      }
    });
  },
};

/**
 * Converts a PDF file to markdown.
 * Shows an error dialog and returns null when the PDF can't be read, has no text or is too large.
 * @param {File} file - The PDF file
 * @returns {Promise<string|null>} - The markdown content extracted from the PDF
 */
export async function pdfFileToMarkdown(file) {
  let markDown;

  try {
    markDown = await pdf2md(await file.arrayBuffer());
  } catch (err) {
    console.error("Error during PDF to markdown conversion", err);
    Dialog.alertError(getUITranslation("Une erreur est survenue."));
    return null;
  }

  if (!markDown || markDown.trim().length === 0) {
    console.error("This PDF file appears to be empty or contains no extractable text.");
    Dialog.alertError(
      getUITranslation("Ce fichier PDF ne contient pas de texte exploitable."),
      getUITranslation("Il s'agit peut-être d'un PDF scanné ou d'une image. Veuillez sélectionner un autre fichier.")
    );
    return null;
  }

  if (markDown.length >= HUGE_PDF_LIMIT) {
    Dialog.alertError(
      getUITranslation("Le fichier PDF est trop volumineux."),
      getUITranslation("Veuillez sélectionner un autre fichier ou réduire la taille du PDF.")
    );
    return null;
  }

  return markDown;
}

/**
 * Reads a pdf file from input and converts it to markdown.
 * Set the markdown content to markdownInput and enable the submit button if the conversion is successful.
 *
 * When the input opts in with `data-upload-file`, the PDF is also uploaded and its metadata posted
 * alongside the markdown: importers that keep the original document need the file itself,
 * which the markdown conversion throws away.
 *
 * @param {HTMLInputElement} inputElement - The file input element that triggered the change event
 * @param {HTMLFormElement} form - The form element
 * @returns {Promise<string>} - The markdown content extracted from the PDF
 */
async function onChangeImporterPDFInput(inputElement, form) {
  const file = inputElement.files?.[0];

  // Picking another file drops what the previous one left: submitting while this one converts or
  // uploads, or after its upload failed, would pair its markdown with the previous file.
  form.querySelector("[type='submit']")?.setAttribute("disabled", "");
  form.querySelector("[data-action='import-to-chat']")?.setAttribute("disabled", "");
  const fileInput = form.querySelector("[name='importer-file']");
  if (fileInput) fileInput.value = "";

  if (!file) {
    Dialog.alertError(getUITranslation("Une erreur est survenue."), getUITranslation("Veuillez réessayer."));
    throw new Error("No file selected");
  }

  const markDown = await pdfFileToMarkdown(file);
  if (!markDown) throw new Error("PDF conversion failed");

  const markdownInput = form.querySelector("[name='importer-markdown']");
  const submitButton = form.querySelector("[type='submit']");

  if (!markdownInput) throw new Error("markdownInput not found in DOM");
  if (!submitButton) throw new Error("submitButton not found in DOM");

  markdownInput.value = markDown;

  if (inputElement.dataset.uploadFile === "true" && fileInput) {
    try {
      fileInput.value = JSON.stringify(await uploadFile(file));
    } catch (err) {
      console.error("Error during PDF upload", err);
      Dialog.alertError(getUITranslation("Le document n'a pas pu être conservé. L'import continue sans la pièce jointe."));
    }
  }

  // Enabled last: submitting before the upload resolves would lose the attachment.
  submitButton.removeAttribute("disabled");

  const filenameInput = form.querySelector("[name='importer-filename']");
  if (filenameInput) filenameInput.value = file.name;

  const toChatButton = form.querySelector("[data-action='import-to-chat']");
  if (toChatButton) toChatButton.removeAttribute("disabled");

  return markDown;
}

export default hook;
